import { useEffect } from 'react';

const typographyTags = new Set([
  'P',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'SPAN',
  'B',
  'I',
  'U',
  'SMALL',
  'EM',
  'STRONG',
  'MARK',
  'DEL',
  'INS',
  'SUB',
  'SUP',
]);

const highlightText = (regex: RegExp, elements: HTMLElement[]): void => {
  const traverseNodes = (node: Node): void => {
    if (
      node.nodeType === Node.TEXT_NODE
      && node.nodeValue?.trim()
      && regex.test(node.nodeValue)
      && node.parentElement
      && !node.parentElement.closest('search-highlight')
      && typographyTags.has(node.parentElement.tagName)
    ) {
      const span = document.createElement('span');
      span.innerHTML = node.nodeValue.replace(regex, `<search-highlight>$1</search-highlight>`);

      const { parentNode } = node;
      if (parentNode && parentNode.contains(node)) {
        parentNode.replaceChild(span, node);
      }
    } else if (node.childNodes.length > 0) {
      Array.from(node.childNodes).forEach(childNode => traverseNodes(childNode));
    }
  };
  elements.forEach(element => {
    traverseNodes(element);
  });
};

const removeHighlights = (elements: HTMLElement[]): void => {
  const traverseAndRemoveHighlights = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.tagName === 'SEARCH-HIGHLIGHT') {
        const parent = element.parentNode;
        if (parent) {
          const textNode = document.createTextNode(element.textContent || '');
          parent.replaceChild(textNode, element);
          parent.normalize();
        }
      } else {
        node.childNodes.forEach(childNode => traverseAndRemoveHighlights(childNode));
      }
    }
  };

  elements.forEach(element => {
    traverseAndRemoveHighlights(element);
  });
};

const escapeSpecialChars = (string: string): string => string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');

const useHighlightSearchTerm = (searchTerm: string, containers: HTMLElement[]): void => {
  useEffect(() => {
    const elements = containers.filter(Boolean);
    if (searchTerm.length < 3) {
      removeHighlights(elements);
      return () => {};
    }

    const escapedSearchTerm = escapeSpecialChars(searchTerm);
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

    let observer: MutationObserver;

    const observeElements = (): void => {
      elements.forEach(element => {
        observer.observe(element, { childList: true, subtree: true });
      });
    };

    observer = new MutationObserver(() => {
      observer.disconnect();
      highlightText(regex, elements);
      observeElements();
    });

    highlightText(regex, elements);
    observeElements();

    return () => {
      observer.disconnect();
      removeHighlights(elements);
    };
  }, [searchTerm, containers]);
};

export default useHighlightSearchTerm;
